import { WorkOrderReferenceNumberField } from "@/services/backend/htz/work-order/components/work-order-reference-number-field";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { ServiceAddressCard } from "@/services/backend/addresses/components/service-address-card";
import { Label } from "@/shared/components/ui/label";
import { WorkOrder } from "@/services/backend/htz/work-order/types/work-order";
import { ContactCard } from "@/services/backend/contacts/components/contact-card";
import { RepresentativeCard } from "@/services/backend/representatives/components/representative-card";
import {
  PatchRequest,
  updateHtzWorkOrderCache,
  useHtzWorkOrderPatchMutation,
  useHtzWorkOrderShowQuery,
} from "@/services/backend/htz/work-order/api/work-order-api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useListBranchQuery } from "@/services/backend/company/api/branch";
import { TextInputField } from "@/shared/components/field/input-field";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { FieldErrors } from "@/shared/components/field/field";
import { ConnectDocumentDataCard } from "@/services/backend/connect/components/connect-document-data-card";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CardSkeleton } from "@/shared/components/card-skeleton";

export function WorkOrderData({ workOrderId }: { workOrderId: string }) {
  const { data, error, isLoading } = useHtzWorkOrderShowQuery({
    id: workOrderId,
  });

  return (
    <div>
      {data && <Data data={data} />}
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <CardSkeleton />}
    </div>
  );
}

function Data({ data }: { data: WorkOrder }) {
  const request: PatchRequest = { id: data.id };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzWorkOrderCache,
    useHtzWorkOrderPatchMutation(),
  );
  const disabled = isImmutable(data);

  return (
    <div className="space-y-2">
      <Card>
        <CardHeader>
          <CardTitle>{t("Arbeitsschein")}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4">
          <WorkOrderReferenceNumberField workOrderId={data.id} />
          <ExecutingBranchSelect
            workOrder={data}
            onExecutingBranchIdChange={(executingBranchId) =>
              set(
                { ...request, executingBranchId },
                { ...data, executingBranchId },
              )
            }
            disabled={disabled}
          />
          <div className="grid grid-cols-2 gap-4">
            <DatePickerField
              label={t("Arbeitsbeginn")}
              nullable={true}
              date={data.beginDate ?? undefined}
              onDateChange={(beginDate) =>
                set(
                  { ...request, beginDate: beginDate ?? null },
                  { ...data, beginDate },
                )
              }
              fieldName="beginDate"
              fieldProps={{ className: "content-start" }}
              disabled={disabled}
            />
            <DatePickerField
              label={t("Arbeitsende")}
              nullable={true}
              date={data.endDate ?? undefined}
              onDateChange={(endDate) =>
                set(
                  { ...request, endDate: endDate ?? null },
                  { ...data, endDate },
                )
              }
              fieldName="endDate"
              fieldProps={{ className: "content-start" }}
              disabled={disabled}
            />
          </div>
          <TextInputField
            label={t("Teamleiter")}
            text={data.teamLeader}
            onTextChange={(teamLeader) =>
              set({ ...request, teamLeader }, { ...data, teamLeader })
            }
            fieldName="teamLeader"
            disabled={disabled}
          />
          <TextareaField
            label={t("Interner Kommentar")}
            value={data.internalComment}
            onChange={(e) =>
              set(
                { ...request, internalComment: e.target.value },
                { ...data, internalComment: e.target.value },
              )
            }
            fieldName="internalComment"
            disabled={disabled}
          />
        </CardContent>
      </Card>
      <ConnectDocumentDataCard
        serviceKind={GSMServiceKind.HTZ}
        documentKind={GSMDocumentKind.WorkOrder}
        documentId={data.id}
      />
      <ServiceAddressCard
        address={data.address}
        onAddressChange={(address) =>
          set({ ...request, address }, { ...data, address })
        }
        fieldName="address"
        disabled={disabled}
      />
      <div className="grid gap-2 md:grid-cols-2">
        <ContactCard
          contact={data.contact}
          onContactChange={(contact) =>
            set({ ...request, contact }, { ...data, contact })
          }
          fieldName="contact"
          disabled={disabled}
        />
        <RepresentativeCard
          representative={data.representative}
          onRepresentativeChange={(representative) =>
            set({ ...request, representative }, { ...data, representative })
          }
          fieldName="representative"
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function ExecutingBranchSelect({
  workOrder,
  onExecutingBranchIdChange,
  disabled,
}: {
  workOrder: WorkOrder;
  onExecutingBranchIdChange: (executingBranchId: string) => void;
  disabled: boolean;
}) {
  const { errors, invalid, reset } = useFieldErrors("executingBranchId");

  const onBranchIdChange = (branchId: string) => {
    reset();
    onExecutingBranchIdChange(branchId);
  };

  const { data: list } = useListBranchQuery({
    company: { active: true, values: [workOrder.companyId] },
  });
  const branches = list?.branches ?? [];

  return (
    <div className="grid gap-1.5">
      <Label>{t("Ausführende Niederlassung")}</Label>
      <Select
        value={workOrder.executingBranchId}
        onValueChange={onBranchIdChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-full" aria-invalid={invalid || undefined}>
          <SelectValue placeholder={t("Auswählen...")} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>{t("Niederlassung")}</SelectLabel>
            {branches.map((branch) => (
              <SelectItem key={branch.id} value={branch.id}>
                {branch.name}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <FieldErrors errors={errors} />
    </div>
  );
}
